import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Dialog, Input, Text } from '@plurall/elo-beta'

import {
  CHECK_MOBILE,
  CHECK_MOBILE_CODE,
  REQUEST_MOBILE_CODE,
  UPDATE_PERSON_MOBILE,
} from 'graphql/mutations'
import { graphqlClient, handleGraphQLErrors } from 'utils'

import { mobileRemoveMask, renderToastSuccess, renderValidationText } from '../../utils/functions'
import { MAXIMUM_CODE_LENGTH, MESSAGES } from '../../utils/constants'

import styles from '../../PersonalData.module.css'

const DialogEditCellphone = ({ data, openConfirm, onClose, setMobile: saveMobile }) => {
  const [mobile, setMobile] = useState('')
  const [error, setError] = useState(null)
  const [isLoadingCheck, setIsLoadingCheck] = useState(false)
  const [isLoadingCode, setIsLoadingCode] = useState(false)

  const checkMobile = async () => {
    try {
      setIsLoadingCheck(true)
      setIsLoadingCode(true)

      await graphqlClient().mutate({
        mutation: CHECK_MOBILE,
        variables: { mobile: mobileRemoveMask(mobile) },
      })
      setError(null)
      sendMobileCode()
    } catch (error) {
      if (error.graphQLErrors) {
        setError(handleGraphQLErrors(error.graphQLErrors))
      }
    } finally {
      setIsLoadingCheck(false)
      setIsLoadingCode(false)
    }
  }

  const sendMobileCode = async () => {
    const firstName = data.name.split(' ')

    await graphqlClient().mutate({
      mutation: REQUEST_MOBILE_CODE,
      variables: { name: firstName[0], mobile: mobileRemoveMask(mobile) },
    })

    saveMobile(mobile)
    openConfirm()
  }

  const isDisabled = !mobile

  return (
    <Dialog
      title='Editar número de celular cadastrado'
      primaryButtonText='Continuar'
      onPrimaryClick={checkMobile}
      onClose={onClose}
      primaryButtonDisabled={isDisabled}
      primaryButtonLoading={isLoadingCheck || isLoadingCode}
      closePrimaryClick={false}
    >
      <Input
        placeholder='Insira o número de celular'
        onChange={e => setMobile(e)}
        type='cellphone'
        dataTestId='input-newMobile'
        {...renderValidationText(error, 'negative', error)}
        {...renderValidationText(isLoadingCheck, 'informative', MESSAGES.checking_mobile)}
      />
    </Dialog>
  )
}

const DialogConfirmCellphone = ({ data, mobile, backToEdit, onClose, setUpdatedMobile }) => {
  const [error, setError] = useState(null)
  const [code, setCode] = useState('')
  const [showBtnResend, setShowBtnResend] = useState(false)
  const [isLoadingCheck, setIsLoadingCheck] = useState(false)
  const [isLoadingupdate, setIsLoadingupdate] = useState(false)

  useEffect(() => {
    handleBtnResend()
  }, [])

  const handleBtnResend = () => {
    setShowBtnResend(false)
    setTimeout(() => {
      setShowBtnResend(true)
    }, 60000)
  }

  const resendMobileCode = async () => {
    const firstName = data.name.split(' ')

    await graphqlClient().mutate({
      mutation: REQUEST_MOBILE_CODE,
      variables: { name: firstName[0], mobile: mobileRemoveMask(mobile) },
    })

    handleBtnResend()
  }

  const checkCode = async () => {
    try {
      setIsLoadingCheck(true)
      setIsLoadingupdate(true)

      await graphqlClient().mutate({
        mutation: CHECK_MOBILE_CODE,
        variables: { mobile: mobileRemoveMask(mobile), code: Number(code) },
      })

      setError(null)
      updateMobile()
    } catch (error) {
      if (error.graphQLErrors) {
        setError(handleGraphQLErrors(error.graphQLErrors))
      }
    } finally {
      setIsLoadingCheck(false)
      setIsLoadingupdate(false)
    }
  }

  const updateMobile = async () => {
    await graphqlClient().mutate({
      mutation: UPDATE_PERSON_MOBILE,
      variables: { mobile: mobileRemoveMask(mobile), mobileCode: Number(code) },
    })

    setUpdatedMobile(mobile)
    onClose()
    renderToastSuccess()
  }

  const isDisabled = !code

  return (
    <Dialog
      title='Confirmação do número de celular'
      primaryButtonText='Continuar'
      secondaryButtonText='Voltar'
      onSecondaryClick={backToEdit}
      onClose={onClose}
      primaryButtonDisabled={isDisabled}
      onPrimaryClick={checkCode}
      primaryButtonLoading={isLoadingCheck || isLoadingupdate}
      closePrimaryClick={false}
    >
      <div className={styles['dialog-info']}>
        <Text dimmed>
          Enviamos um SMS com um código de 4 números para o número de celular digitado.
        </Text>
      </div>
      <Input
        placeholder='Insira o código recebido'
        onChange={e => setCode(e)}
        type='number'
        hideSpinner
        dataTestId='input-mobileCode'
        maxLength={MAXIMUM_CODE_LENGTH}
        {...renderValidationText(error, 'negative', error)}
        {...renderValidationText(isLoadingCheck, 'informative', MESSAGES.checking_code)}
      />
      {showBtnResend && (
        <div className={styles['dialog-resend-code']}>
          <Text element='a' size='t3' onClick={resendMobileCode} dataTestId='btn-resend'>
            Reenviar código
          </Text>
        </div>
      )}
    </Dialog>
  )
}

DialogEditCellphone.propTypes = {
  openConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }),
  setMobile: PropTypes.func.isRequired,
}

DialogConfirmCellphone.propTypes = {
  backToEdit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }),
  mobile: PropTypes.string,
  setUpdatedMobile: PropTypes.func.isRequired,
}

export { DialogEditCellphone, DialogConfirmCellphone }
